/**
 * §9.9 — the three statements, on one screen.
 *
 * WHY ONE SCREEN AND NOT THREE NAV ITEMS
 *
 * They answer one question between them and are read together: net income
 * without the balance sheet is a number with no context, and a trial balance is
 * what you open when one of the other two looks wrong. Three destinations in a
 * bar that already carries eight would also push the operator into choosing a
 * report before choosing a period, which is backwards -- the period is the same
 * for all three, so it lives above the tabs and survives switching between them.
 *
 * THE COMPLETENESS LINE IS NOT A FOOTNOTE
 *
 * Read from production the day this was built: 72 posted entries against 220
 * uncoded bank transactions worth nearly $200,000 of movement. Every figure on
 * this page is correct and the page is a fraction of the truth. That fact
 * belongs above the numbers, in the same size as the numbers, because a person
 * reading a P&L is not looking for a caveat at the bottom.
 *
 * IT SAYS ACCRUAL, EVERY TIME
 *
 * These read `entry_date`. The cash-basis conversion §6.4 owns is a different
 * screen, and for an S-corp filing on the cash basis the difference is the
 * whole tax position. A report that did not name its basis would be the one
 * mistake here that costs money rather than time, so the basis is stated on the
 * page and there is a link to the other one.
 */

import { filesForm990Pf, keepsNetAssets, offersOwnersEquity, offersPartnersCapital, offersShareholderReport, projectWord, useBusiness, useCan, useKeepsGoods } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ApiError, api, type BalanceSheetReport, type FundBalancesReport, type FunctionalExpensesReport, type GivingReport as GivingReportData,
  type IncomeStatementReport, type Pf990Worksheet, type ProjectReport, type ReturnLineForm, type StockOnHand, type GoodsByProgram, type ReportAccount, type ReportCompleteness, type StatementOfActivities, type StatementOfFinancialPosition,
  type TrialBalanceReport } from '../api.js';
import { ActivitiesStatement, FinancialPositionStatement, FundBalancesTable } from '../components/NonprofitStatements.js';
import { FunctionalExpensesStatement, ProjectReportTable } from '../components/ProjectReports.js';
import { LabelReportView } from '../components/LabelReport.js';
import { PhaseReportView } from '../components/PhaseReport.js';
import { GivingReport } from '../components/GivingReport.js';
import { Form990PfReport } from '../components/Form990PfReport.js';
import { ReturnLinesView } from '../components/ReturnLinesReport.js';
import { StockOnHandReport } from '../components/StockOnHandReport.js';
import { GoodsByProgramReport } from '../components/GoodsByProgramReport.js';
import { formatCents } from '../lib/money.js';
import { PrintButton, PrintHeading } from '../components/PrintReport.js';

type Tab = 'income' | 'balance' | 'functional' | 'funds' | 'projects' | 'phases' | 'labels' | 'giving' | 'form990pf' | 'returns' | 'stock' | 'goods' | 'trial';

const TABS: Partial<Record<Tab, string>> = {
  income: 'Income Statement',
  balance: 'Balance Sheet',
  // Present once the business has a project (N4b): the API answers 404 until then.
  projects: 'Projects',
  // 0091: by phase, once the business has one.
  phases: 'Phases',
  // 0090: one label at a time, once the business has a label this fiscal year.
  labels: 'Labels',
  // 0086: goods on hand, for a business that keeps them.
  stock: 'Stock On Hand',
  // 0088: goods charged to each project, for a business that keeps goods and has a project.
  goods: 'Goods Used By Project',
  trial: 'Trial Balance',
};

/*
 * A non-profit's statements in the same places (N3): the activity for a
 * period where the income statement was, the position at a date where the
 * balance sheet was, and its funds beside them. The tab keys stay, so a link
 * carries the same `report` whichever kind of business reads it.
 */
const NONPROFIT_TABS: Partial<Record<Tab, string>> = {
  income: 'Statement of Activities',
  balance: 'Statement of Financial Position',
  // N4b: expenses by function, and revenue and cost by program.
  functional: 'Functional Expenses',
  funds: 'Fund Balances',
  projects: 'Programs',
  phases: 'Phases',
  labels: 'Labels',
  // N6 (0084): the year's gifts by donor, for someone who may see donor giving.
  giving: 'Giving',
  // N8 (0085): a private foundation's 990-PF worksheet, for its fiscal year.
  form990pf: 'Form 990-PF',
  // 0092 (§B.20): the lines of the return the business files, for a return that has lines.
  returns: 'Return Lines',
  // 0086: goods on hand, for a business that keeps them.
  stock: 'Stock On Hand',
  // 0088 (§15.7): goods given out, by program.
  goods: 'Goods Distributed',
  trial: 'Trial Balance',
};

/**
 * The calendar year to date, which is the period this is nearly always read
 * for: an S-corp files on the calendar year, and §6.9's fiscal-year setting
 * does not exist yet.
 *
 * A DEFAULT PERIOD ON THE SCREEN IS NOT THE SAME AS ONE IN THE API. The API
 * refuses to guess, deliberately -- a report that silently picks its own period
 * is one the operator can misread without ever seeing a wrong number. Here the
 * dates are in the URL and in two visible boxes, so the guess is stated and can
 * be changed.
 */
function yearToDate(): { from: string; to: string } {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return { from: `${year}-01-01`, to: `${year}-${month}-${day}` };
}

/**
 * §9.9 — where a figure drills down to.
 *
 * The window travels with the link, exactly as the statement used it: from and
 * to for the income statement and the trial balance, only `to` for the balance
 * sheet, which is cumulative. A drill-down read over a different window would
 * list lines that do not add up to the figure clicked. `report` is carried so
 * Back returns to the tab the operator came from.
 */
function linesHref(accountId: string, report: Tab, window: { from?: string; to: string }): string {
  const params = new URLSearchParams({ account: accountId, report, to: window.to });
  if (window.from) params.set('from', window.from);
  return `/reports/account?${params.toString()}`;
}

/** An account row, indented by its place in the chart, linked to its lines. */
function AccountRow({
  account,
  amountCents,
  href,
}: {
  account: ReportAccount;
  amountCents: number;
  href: string;
}) {
  return (
    <tr>
      <td style={{ paddingLeft: `${account.depth * 1.5}rem` }}>
        <Link to={href}>
          {account.code} {account.name}
        </Link>
      </td>
      <td className="num">{formatCents(amountCents)}</td>
    </tr>
  );
}

/**
 * What is missing from the figures above it.
 *
 * Rendered even when nothing is outstanding, and that is the point: "nothing
 * uncoded" is information, and a line that appears only when there is bad news
 * teaches the reader to skim past it when it does appear.
 */
function Completeness({ completeness }: { completeness: ReportCompleteness }) {
  const { uncodedCount, uncodedAbsCents, codedDraftCount } = completeness;

  if (uncodedCount === 0 && codedDraftCount === 0) {
    return (
      <p className="completeness is-clear" role="status">
        Every transaction is categorized and posted. These figures are the whole ledger.
      </p>
    );
  }

  return (
    <p className="completeness" role="status">
      <strong>Not everything is in these figures.</strong>{' '}
      {uncodedCount > 0 && (
        <>
          {uncodedCount} bank {uncodedCount === 1 ? 'transaction is' : 'transactions are'} not
          categorized yet, {formatCents(uncodedAbsCents)} of movement.{' '}
        </>
      )}
      {codedDraftCount > 0 && (
        <>
          {codedDraftCount} categorized {codedDraftCount === 1 ? 'entry is' : 'entries are'} waiting to
          be posted.{' '}
        </>
      )}
      <Link to="/triage?view=uncategorized">Go and categorize them</Link>.
    </p>
  );
}

export function ReportsPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const can = useCan();
  const seesPayroll = can('view_payroll');
  // An S corporation's report (0078, 0094); a business taxed with its owner has the owner's equity statement.
  const business = useBusiness();
  const hasShareholders = offersShareholderReport(business);
  const hasOwnersEquity = offersOwnersEquity(business);
  // 0096: a partnership's partners' capital statement.
  const hasPartnersCapital = offersPartnersCapital(business);
  // A non-profit reads its own statements (N3); the API answers them for it alone.
  const nonprofit = keepsNetAssets(business);
  const word = projectWord(business);
  // The Projects tab only for a business that has one (N4b); a non-profit always has its default program.
  const [projectsReport, setProjectsReport] = useState<ProjectReport | null>(null);
  const [functional, setFunctional] = useState<FunctionalExpensesReport | null>(null);
  // 0084: the giving report, for someone granted it (view_donors); the API refuses anyone else.
  const seesGiving = nonprofit && can('view_donors');
  const [giving, setGiving] = useState<GivingReportData | null>(null);
  // 0085: a private foundation's worksheet, read by anyone signed in; its markings by whoever manages the business.
  const privateFoundation = filesForm990Pf(business);
  // 0092: Return Lines for a return with lines; the 990-N has none.
  const annualReturn = business?.annualReturn ?? null;
  const returnForm: ReturnLineForm | null = annualReturn === '990' || annualReturn === '990_ez' || annualReturn === '990_pf' ? annualReturn : null;
  const [worksheet, setWorksheet] = useState<Pf990Worksheet | null>(null);
  // 0086: Stock On Hand, as of the day the period ends, for a business that keeps goods.
  const keepsGoods = useKeepsGoods();
  const [stock, setStock] = useState<StockOnHand | null>(null);
  // 0088: goods by program; null until read, and for a business with no project (the API answers 404).
  const [goods, setGoods] = useState<GoodsByProgram | null>(null);
  // 0090: the Labels tab once this fiscal year has a label; read on each visit, as the projects are.
  const [labelsOffered, setLabelsOffered] = useState(false);
  // 0091: the Phases tab once the business has a phase.
  const [phasesOffered, setPhasesOffered] = useState(false);
  useEffect(() => {
    let live = true;
    api
      .listLabels()
      .then((result) => live && setLabelsOffered(result.labels.length > 0))
      .catch(() => {});
    api
      .listPhases()
      .then((result) => live && setPhasesOffered(result.phases.length > 0))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  const tabs = Object.fromEntries(
    Object.entries(nonprofit ? NONPROFIT_TABS : TABS).filter(
      ([key]) =>
        (key !== 'projects' || projectsReport !== null) &&
        (key !== 'labels' || labelsOffered) &&
        (key !== 'phases' || phasesOffered) &&
        (key !== 'giving' || seesGiving) &&
        (key !== 'form990pf' || privateFoundation) &&
        (key !== 'returns' || returnForm !== null) &&
        (key !== 'stock' || keepsGoods) &&
        (key !== 'goods' || goods !== null),
    ),
  ) as Partial<Record<Tab, string>>;
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = yearToDate();

  const asked = searchParams.get('report') as Tab | null;
  const tab: Tab = asked !== null && asked in tabs ? asked : 'income';
  const from = searchParams.get('from') ?? defaults.from;
  const to = searchParams.get('to') ?? defaults.to;

  const [income, setIncome] = useState<IncomeStatementReport | null>(null);
  const [balance, setBalance] = useState<BalanceSheetReport | null>(null);
  const [trial, setTrial] = useState<TrialBalanceReport | null>(null);
  const [activities, setActivities] = useState<StatementOfActivities | null>(null);
  const [position, setPosition] = useState<StatementOfFinancialPosition | null>(null);
  const [fundsReport, setFundsReport] = useState<FundBalancesReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  /*
   * Every tab's report is fetched together whenever the period changes, rather
   * than one per tab.
   *
   * They are read against each other -- net income here should be the earnings
   * line there -- and a tab that fetched on selection would let the operator
   * compare a figure from one period with a figure from another after editing
   * the dates. A few small reads, one period, one moment.
   */
  const load = useCallback(() => {
    setLoading(true);
    // The Projects report only for a business that has a project (review RP-1):
    // asking every business would answer 404 on every visit, which a reader of
    // the request log cannot tell from a broken link. The list is read on each
    // load, so a project added in Settings shows its tab without signing in again.
    const projects = api
      .listProjects()
      .then(({ projects: list }) => (list.length > 0 ? api.projectReport(from, to) : null));
    if (nonprofit) {
      Promise.all([
        api.statementOfActivities(from, to),
        api.statementOfFinancialPosition(to),
        api.fundBalances(from, to),
        api.trialBalance(to, from),
        api.functionalExpenses(from, to),
        projects,
        // The year the period ends in: gifts are a donor's calendar year.
        seesGiving ? api.givingReport(Number(to.slice(0, 4))) : Promise.resolve(null),
        // The fiscal year the period ends in (PF-1): the API resolves it from the date.
        privateFoundation ? api.form990Pf(to) : Promise.resolve(null),
      ])
        .then(([nextActivities, nextPosition, nextFunds, nextTrial, nextFunctional, nextProjects, nextGiving, nextWorksheet]) => {
          setGiving(nextGiving);
          setWorksheet(nextWorksheet);
          setActivities(nextActivities);
          setPosition(nextPosition);
          setFundsReport(nextFunds);
          setTrial(nextTrial);
          setFunctional(nextFunctional);
          setProjectsReport(nextProjects);
          setError(null);
        })
        .catch((caught: unknown) =>
          setError(caught instanceof ApiError ? caught.message : 'Could not build the reports.'),
        )
        .finally(() => setLoading(false));
      return;
    }
    Promise.all([
      api.incomeStatement(from, to),
      api.balanceSheet(to),
      api.trialBalance(to, from),
      projects,
    ])
      .then(([nextIncome, nextBalance, nextTrial, nextProjects]) => {
        setIncome(nextIncome);
        setBalance(nextBalance);
        setTrial(nextTrial);
        setProjectsReport(nextProjects);
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not build the reports.'),
      )
      .finally(() => setLoading(false));
  }, [from, to, nonprofit, seesGiving, privateFoundation]);

  /*
   * 0086: Stock On Hand is a position at a date, read beside the period's
   * reports rather than inside their Promise.all, so a business that keeps no
   * goods asks for nothing and one that does is not held up by it.
   */
  useEffect(() => {
    if (!keepsGoods) return;
    let current = true;
    api
      .stockOnHand(to)
      .then((next) => current && setStock(next))
      .catch((caught: unknown) => current && setError(caught instanceof ApiError ? caught.message : 'Could not read the stock on hand.'));
    return () => {
      current = false;
    };
  }, [keepsGoods, to]);

  // 0088: goods by program, for a business that keeps goods; a 404 is a business with no project yet, and no tab.
  useEffect(() => {
    if (!keepsGoods) return;
    let current = true;
    api
      .goodsByProgram(from, to)
      .then((next) => current && setGoods(next))
      .catch((caught: unknown) => {
        if (!current) return;
        if (caught instanceof ApiError && caught.status === 404) setGoods(null);
        else setError(caught instanceof ApiError ? caught.message : 'Could not read the goods by program.');
      });
    return () => {
      current = false;
    };
  }, [keepsGoods, from, to]);

  useEffect(load, [load]);

  function setPeriod(next: { from?: string; to?: string }) {
    const params = new URLSearchParams(searchParams);
    // Written explicitly rather than left to the default, so a link to this
    // page carries the period it was read at.
    params.set('from', next.from ?? from);
    params.set('to', next.to ?? to);
    setSearchParams(params, { replace: true });
  }

  /*
   * §9.9 — the books for the CPA, for the page's period: the trial balance and
   * journal entries over From to To, and the whole chart. The file is built on
   * the server; this only saves what comes back.
   */
  async function exportBooks() {
    setExporting(true);
    try {
      const file = await api.exportBooks(to, from);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `books-${from}-to-${to}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked later rather than at once: a browser that starts the download
      // asynchronously must still find the object behind the link.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not export the books.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className="reports">
      {/* Each statement names its own period under its heading. */}
      <PrintHeading />
      <header className="row reports-head">
        <h1>Reports</h1>
        {/*
          The basis, stated on the page and not in a tooltip. For an S-corp
          filing on the cash basis this is the difference between a number to
          act on and a number to ignore.
        */}
        <span className="muted">
          Accrual basis, by entry date. <Link to="/reports/cash-basis">Cash basis</Link> is its own
          report.
        </span>
        {/* The two ways a report leaves the screen, side by side. */}
        <span className="row report-actions">
          <button type="button" onClick={() => void exportBooks()} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
          <PrintButton />
        </span>
      </header>

      <nav className="row tabs" aria-label="Report">
        {(Object.keys(tabs) as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`tab${tab === key ? ' is-current' : ''}`}
            aria-current={tab === key}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('report', key);
              setSearchParams(params, { replace: true });
            }}
          >
            {tabs[key]}
          </button>
        ))}
        {/*
          A Link, not a button, because Cashflow is its own address. The other
          three share this page's From/To period; a forecast has a horizon
          instead, and carrying a year-to-date period onto it would open a
          window that ended before it started.
        */}
        <Link to="/reports/cashflow" className="tab">
          Cashflow
        </Link>
        {/* It names an officer's wages, so it is payroll's; and it is an S corporation's. */}
        {seesPayroll && hasShareholders && (
          <Link to="/reports/shareholder" className="tab">
            Shareholder
          </Link>
        )}
        {hasOwnersEquity && (
          <Link to="/reports/owners-equity" className="tab">
            Owner&apos;s Equity
          </Link>
        )}
        {hasPartnersCapital && (
          <Link to="/reports/partners-capital" className="tab">
            Partners&apos; Capital
          </Link>
        )}
      </nav>

      <div className="row report-period">
        <label>
          {/* The balance sheet ignores the start date, and says so rather than
              hiding the control and making the tabs jump. */}
          From{' '}
          <input
            type="date"
            value={from}
            disabled={tab === 'balance'}
            onChange={(event) => setPeriod({ from: event.target.value })}
          />
        </label>
        <label>
          {tab === 'balance' ? 'As of' : 'To'}{' '}
          <input
            type="date"
            value={to}
            onChange={(event) => setPeriod({ to: event.target.value })}
          />
        </label>
        {tab === 'balance' && (
          <span className="muted">A balance sheet is cumulative, so it has no start date.</span>
        )}
      </div>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {loading && <p className="muted">Working…</p>}

      {!loading && !error && nonprofit && activities && position && fundsReport && trial && (
        <>
          <Completeness completeness={activities.completeness} />
          {tab === 'income' && <ActivitiesStatement statement={activities} />}
          {tab === 'balance' && <FinancialPositionStatement statement={position} />}
          {tab === 'functional' && functional && <FunctionalExpensesStatement report={functional} />}
          {tab === 'giving' && giving && <GivingReport report={giving} />}
          {tab === 'form990pf' && worksheet && (
            <Form990PfReport
              worksheet={worksheet}
              canMark={can('manage_business')}
              // The worksheet alone is read again, so the open markings stay on screen with their sentence.
              onChanged={() => {
                api
                  .form990Pf(to)
                  .then(setWorksheet)
                  .catch((caught: unknown) => setError(caught instanceof ApiError ? caught.message : 'Could not build the reports.'));
              }}
            />
          )}
          {tab === 'returns' && <ReturnLinesView asOf={to} preferred={returnForm} canEdit={can('manage_business')} />}
          {tab === 'funds' && <FundBalancesTable report={fundsReport} />}
          {tab === 'projects' && projectsReport && <ProjectReportTable report={projectsReport} word={word} />}
          {tab === 'labels' && <LabelReportView from={from} to={to} word={word} />}
          {tab === 'phases' && <PhaseReportView from={from} to={to} word={word} initialProjectId={searchParams.get('project')} />}
          {tab === 'stock' && stock && <StockOnHandReport report={stock} />}
          {tab === 'goods' && goods && (
            <GoodsByProgramReport report={goods} word={word} title={nonprofit ? 'Goods Distributed By Program' : `Goods Used By ${word}`} />
          )}
          {tab === 'trial' && <TrialBalanceView trial={trial} />}
        </>
      )}

      {!loading && !error && !nonprofit && income && balance && trial && (
        <>
          <Completeness completeness={income.completeness} />

          {tab === 'income' && (
            <div className="report">
              <h2>Income Statement</h2>
              <p className="muted">
                {income.from} to {income.to}
              </p>

              <table className="lines">
                <tbody>
                  <tr className="report-section">
                    <th colSpan={2}>Revenue</th>
                  </tr>
                  {income.revenue.map((account) => (
                    <AccountRow
                      key={account.accountId}
                      account={account}
                      // Revenue is a credit balance, so the sign is flipped to
                      // read as income earned rather than as a negative.
                      amountCents={-account.netCents}
                      href={linesHref(account.accountId, 'income', { from: income.from, to: income.to })}
                    />
                  ))}
                  {income.revenue.length === 0 && (
                    <tr>
                      <td className="muted" colSpan={2}>
                        No revenue posted in this period.
                      </td>
                    </tr>
                  )}
                  <tr className="report-total">
                    <td>Total revenue</td>
                    <td className="num">{formatCents(income.totalRevenueCents)}</td>
                  </tr>

                  <tr className="report-section">
                    <th colSpan={2}>Expenses</th>
                  </tr>
                  {income.expenses.map((account) => (
                    <AccountRow
                      key={account.accountId}
                      account={account}
                      amountCents={account.netCents}
                      href={linesHref(account.accountId, 'income', { from: income.from, to: income.to })}
                    />
                  ))}
                  {income.expenses.length === 0 && (
                    <tr>
                      <td className="muted" colSpan={2}>
                        No expenses posted in this period.
                      </td>
                    </tr>
                  )}
                  <tr className="report-total">
                    <td>Total expenses</td>
                    <td className="num">{formatCents(income.totalExpenseCents)}</td>
                  </tr>

                  <tr className="report-total is-final">
                    <td>{income.netIncomeCents < 0 ? 'Net loss' : 'Net income'}</td>
                    <td className="num">{formatCents(income.netIncomeCents)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {tab === 'balance' && (
            <div className="report">
              <h2>Balance Sheet</h2>
              <p className="muted">As of {balance.asOf}</p>

              <table className="lines">
                <tbody>
                  <tr className="report-section">
                    <th colSpan={2}>Assets</th>
                  </tr>
                  {balance.assets.map((account) => (
                    <AccountRow
                      key={account.accountId}
                      account={account}
                      amountCents={account.netCents}
                      href={linesHref(account.accountId, 'balance', { to: balance.asOf })}
                    />
                  ))}
                  <tr className="report-total">
                    <td>Total assets</td>
                    <td className="num">{formatCents(balance.totalAssetsCents)}</td>
                  </tr>

                  <tr className="report-section">
                    <th colSpan={2}>Liabilities</th>
                  </tr>
                  {balance.liabilities.map((account) => (
                    <AccountRow
                      key={account.accountId}
                      account={account}
                      amountCents={-account.netCents}
                      href={linesHref(account.accountId, 'balance', { to: balance.asOf })}
                    />
                  ))}
                  <tr className="report-total">
                    <td>Total liabilities</td>
                    <td className="num">{formatCents(balance.totalLiabilitiesCents)}</td>
                  </tr>

                  <tr className="report-section">
                    <th colSpan={2}>Equity</th>
                  </tr>
                  {balance.equity.map((account) => (
                    <AccountRow
                      key={account.accountId}
                      account={account}
                      amountCents={-account.netCents}
                      href={linesHref(account.accountId, 'balance', { to: balance.asOf })}
                    />
                  ))}
                  <tr>
                    {/*
                      NOT "retained earnings". That is the account above it,
                      which holds every year the operator has CLOSED (migration
                      0052); this is revenue and expense no close has moved yet.
                      Before the first close it is every P&L line ever posted,
                      and naming it for a close that has not run would be the
                      report telling a small lie about its own construction.
                    */}
                    <td>Current earnings</td>
                    <td className="num">{formatCents(balance.currentEarningsCents)}</td>
                  </tr>
                  <tr className="report-total">
                    <td>Total liabilities and equity</td>
                    <td className="num">
                      {formatCents(
                        balance.totalLiabilitiesCents +
                          balance.totalEquityCents +
                          balance.currentEarningsCents,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/*
                Which years are in retained earnings and which are current, so
                a reader knows what "Current earnings" spans. Absent until a
                year is closed: before that the sentence would only say that
                nothing has been.
              */}
              {balance.closedFiscalYear !== null && (
                <p className="muted">
                  Fiscal years through {balance.closedFiscalYear} are closed into retained earnings.
                  Current earnings is what has been posted after {balance.closedThrough}.
                </p>
              )}

              {!balance.balanced && (
                <p role="alert" className="error">
                  This balance sheet is out by {formatCents(balance.differenceCents)}. The ledger
                  refuses unbalanced entries, so this is a fault in the report rather than in the
                  books.
                </p>
              )}
            </div>
          )}

          {tab === 'projects' && projectsReport && <ProjectReportTable report={projectsReport} word={word} />}
          {tab === 'labels' && <LabelReportView from={from} to={to} word={word} />}
          {tab === 'phases' && <PhaseReportView from={from} to={to} word={word} initialProjectId={searchParams.get('project')} />}
          {tab === 'stock' && stock && <StockOnHandReport report={stock} />}
          {tab === 'goods' && goods && (
            <GoodsByProgramReport report={goods} word={word} title={nonprofit ? 'Goods Distributed By Program' : `Goods Used By ${word}`} />
          )}
          {tab === 'trial' && <TrialBalanceView trial={trial} />}
        </>
      )}
    </section>
  );
}

/** The trial balance, the same for every kind of business. */
function TrialBalanceView({ trial }: { trial: TrialBalanceReport }) {
  return (
    <div className="report">
      <h2>Trial Balance</h2>
      <p className="muted">
        {trial.from ? `${trial.from} to ${trial.to}` : `Everything through ${trial.to}`}
      </p>

      <table className="lines">
        <thead>
          <tr>
            <th>Account</th>
            <th className="num">Debit</th>
            <th className="num">Credit</th>
          </tr>
        </thead>
        <tbody>
          {trial.accounts.map((account) => (
            <tr key={account.accountId}>
              <td style={{ paddingLeft: `${account.depth * 1.5}rem` }}>
                <Link
                  to={linesHref(account.accountId, 'trial', {
                    from: trial.from ?? undefined,
                    to: trial.to,
                  })}
                >
                  {account.code} {account.name}
                </Link>
              </td>
              <td className="num">
                {account.netCents > 0 ? formatCents(account.netCents) : ''}
              </td>
              <td className="num">
                {account.netCents < 0 ? formatCents(-account.netCents) : ''}
              </td>
            </tr>
          ))}
          {trial.accounts.length === 0 && (
            <tr>
              <td className="muted" colSpan={3}>
                Nothing has been posted in this period.
              </td>
            </tr>
          )}
          <tr className="report-total is-final">
            <td>Total</td>
            <td className="num">{formatCents(trial.totalDebitCents)}</td>
            <td className="num">{formatCents(trial.totalCreditCents)}</td>
          </tr>
        </tbody>
      </table>

      {/*
        Stated either way. A check that only speaks up when it fails is
        a check nobody knows is running, and this one is the reason to
        open this tab at all.
      */}
      {/*
        Pre-closing for the year it ends on (0052; the operator's
        decision on the review's FY-6): the closing entry dated on the
        report's last day is left out, so the year's revenue and
        expense show and 3900 disagrees with the balance sheet by
        exactly that entry on that one day. Said whenever it happens.
      */}
      {trial.closingLeftOut && (
        <p className="muted">
          Before closing entries: the entry closing fiscal {trial.closingLeftOut.fiscalYear}{' '}
          on {trial.closingLeftOut.entryDate} is left out, so revenue and expense show the
          year and retained earnings does not yet include it.
        </p>
      )}
      {trial.balanced ? (
        <p className="muted">Debits equal credits.</p>
      ) : (
        <p role="alert" className="error">
          Debits and credits differ by{' '}
          {formatCents(Math.abs(trial.totalDebitCents - trial.totalCreditCents))}. The
          database refuses unbalanced entries, so something has written lines past the
          services.
        </p>
      )}
    </div>
  );
}
